/**
 * Color Explosion Visualizer - Sharp-edged radial frequency spikes with shockwave rings
 *
 * The polar opposite of Fireworks: no blur, no fade, no trails, no alpha blending.
 * Everything is hard-edged filled polygons at full opacity, instantly responsive.
 * Canvas fully cleared each frame via base class clearCanvas().
 *
 * Three visual layers:
 * 1. Center hexagon (optional) — scales with bass energy
 * 2. Radial frequency spikes — trapezoid wedges driven by per-band amplitude
 * 3. Shockwave rings — expanding segmented rings spawned on beats
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'color-explosion',
  displayName: 'Color Explosion',
  contextType: '2d',
};

const MAX_RINGS = 12;

// Color mode hue/lightness ranges
const COLOR_MODES = {
  vivid:      { hueMin: 0, hueMax: 360, lightMin: 35, lightMax: 60 },
  neon:       { hueMin: 0, hueMax: 360, lightMin: 55, lightMax: 70 },
  fire:       { hueMin: 0, hueMax: 60,  lightMin: 40, lightMax: 65 },
  ice:        { hueMin: 180, hueMax: 270, lightMin: 40, lightMax: 70 },
  monochrome: { hueMin: 0, hueMax: 0,  lightMin: 30, lightMax: 100 },
};

class ColorExplosion extends Base2DVisualizer {
  static name = 'color-explosion';
  static displayName = 'Color Explosion';

  static getConfigUI() {
    return [
      { type: 'range', id: 'spikeCount', label: 'Spike Count', min: 8, max: 64, default: 32 },
      { type: 'range', id: 'innerRadius', label: 'Inner Radius', min: 5, max: 30, default: 12, scale: 0.01 },
      { type: 'range', id: 'spikeWidth', label: 'Spike Width', min: 50, max: 100, default: 85, scale: 0.01 },
      { type: 'range', id: 'ringSegments', label: 'Ring Segments', min: 6, max: 48, default: 24 },
      { type: 'range', id: 'ringSpeed', label: 'Ring Speed', min: 3, max: 20, default: 8 },
      { type: 'range', id: 'ringThickness', label: 'Ring Thickness', min: 5, max: 40, default: 15 },
      { type: 'checkbox', id: 'centerShape', label: 'Center Shape', default: true },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 50, default: 15, scale: 0.01 },
      {
        type: 'select', id: 'colorMode', label: 'Color Mode', default: 'vivid',
        options: [
          { value: 'vivid', label: 'Vivid' },
          { value: 'neon', label: 'Neon' },
          { value: 'fire', label: 'Fire' },
          { value: 'ice', label: 'Ice' },
          { value: 'monochrome', label: 'Monochrome' },
          { value: 'custom', label: 'Custom Colors' }
        ]
      },
      { type: 'color', id: 'colorStart', label: 'Custom Color 1', default: '#ff0044' },
      { type: 'color', id: 'colorEnd', label: 'Custom Color 2', default: '#00ddff' },
    ];
  }

  getDefaultConfig() {
    return {
      colorMode: 'vivid',
    };
  }

  init() {
    // Continuous hue rotation — jumps on beats, drifts between them
    this.hueOffset = 0;

    // Active shockwave rings: { radius, hueBase, segments }
    this.rings = [];

    // Parse custom colors into HSL for interpolation
    this._customHSL1 = null;
    this._customHSL2 = null;
    this._updateCustomColors();
  }

  onConfigChange(changed) {
    if (changed.colorStart !== undefined || changed.colorEnd !== undefined) {
      this._updateCustomColors();
    }
  }

  getElementCount() {
    return this.config.spikeCount;
  }

  /**
   * Parse custom hex colors to HSL for interpolation.
   */
  _updateCustomColors() {
    this._customHSL1 = hexToHSL(this.config.colorStart || '#ff0044');
    this._customHSL2 = hexToHSL(this.config.colorEnd || '#00ddff');
  }

  /**
   * Generate an HSL color string for a given spike index and intensity.
   * @param {number} index - Spike/segment index
   * @param {number} total - Total number of spikes/segments
   * @param {number} intensity - Amplitude (0-1), modulates lightness
   * @returns {string} HSL color string
   */
  _getColor(index, total, intensity) {
    const { colorMode } = this.config;

    if (colorMode === 'custom') {
      return this._getCustomColor(index, total, intensity);
    }

    const mode = COLOR_MODES[colorMode] || COLOR_MODES.vivid;
    const hueRange = mode.hueMax - mode.hueMin;

    // Distribute hues across spikes, rotated by hueOffset
    let hue;
    if (hueRange === 0) {
      // Monochrome — achromatic
      hue = 0;
    } else {
      // Mod hueOffset by hueRange first so beat shifts stay proportional
      // within narrow ranges (fire=60°, ice=90°)
      const rotatedOffset = ((this.hueOffset % hueRange) + hueRange) % hueRange;
      hue = mode.hueMin + ((index / total) * hueRange + rotatedOffset) % hueRange;
    }
    hue = ((hue % 360) + 360) % 360;

    // Intensity modulates lightness — louder = brighter
    const lightness = mode.lightMin + intensity * (mode.lightMax - mode.lightMin);
    const saturation = hueRange === 0 ? 0 : 90;

    return `hsl(${hue | 0}, ${saturation}%, ${lightness | 0}%)`;
  }

  /**
   * Generate color by interpolating between two custom colors.
   */
  _getCustomColor(index, total, intensity) {
    const t = total > 1 ? index / (total - 1) : 0;
    const h1 = this._customHSL1;
    const h2 = this._customHSL2;

    // Interpolate HSL components
    let hue = h1.h + (h2.h - h1.h) * t + this.hueOffset;
    hue = ((hue % 360) + 360) % 360;
    const sat = h1.s + (h2.s - h1.s) * t;
    const baseLightness = h1.l + (h2.l - h1.l) * t;
    // Intensity boosts lightness
    const lightness = baseLightness * 0.6 + baseLightness * 0.4 * intensity;

    return `hsl(${hue | 0}, ${sat | 0}%, ${lightness | 0}%)`;
  }

  /**
   * Main render — draws all three layers each frame.
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const minDim = Math.min(canvas.width, canvas.height);

    // Beat detection — shift palette and spawn ring
    const rhythm = features?.rhythm;
    if (rhythm?.isOnset) {
      const shift = 30 + rhythm.confidence * 20;
      this.hueOffset = (this.hueOffset + shift) % 360;
      this._spawnRing(minDim);
    }

    // Slow continuous hue drift between beats
    this.hueOffset = (this.hueOffset + 0.3) % 360;

    const innerR = minDim * config.innerRadius;
    const maxLength = minDim * 0.42;
    const spikeCount = amplitudes.length;
    const angleStep = (Math.PI * 2) / spikeCount;
    const halfWidth = (angleStep * config.spikeWidth) / 2;

    // --- Layer 1: Center hexagon ---
    if (config.centerShape) {
      const bassAvg = (this.bassEnergy + this.subBassEnergy) / 2;
      const hexRadius = innerR * (0.5 + bassAvg * 0.8);
      const hexHue = (this.hueOffset + 180) % 360;
      const hexLight = 35 + bassAvg * 25;
      ctx.fillStyle = `hsl(${hexHue | 0}, 85%, ${hexLight | 0}%)`;
      this._drawHexagon(cx, cy, hexRadius);
    }

    // --- Layer 2: Radial frequency spikes ---
    for (let i = 0; i < spikeCount; i++) {
      const amp = amplitudes[i];
      if (amp < 0.01) continue; // Skip silent bands

      const angle = angleStep * i - Math.PI / 2 + (this.hueOffset * Math.PI) / 180 * 0.1;
      const outerR = innerR + amp * maxLength;

      // Compute four vertices of the trapezoid wedge
      const a1 = angle - halfWidth;
      const a2 = angle + halfWidth;

      const x1 = cx + Math.cos(a1) * innerR;
      const y1 = cy + Math.sin(a1) * innerR;
      const x2 = cx + Math.cos(a2) * innerR;
      const y2 = cy + Math.sin(a2) * innerR;
      const x3 = cx + Math.cos(a2) * outerR;
      const y3 = cy + Math.sin(a2) * outerR;
      const x4 = cx + Math.cos(a1) * outerR;
      const y4 = cy + Math.sin(a1) * outerR;

      ctx.fillStyle = this._getColor(i, spikeCount, amp);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x3, y3);
      ctx.lineTo(x4, y4);
      ctx.closePath();
      ctx.fill();
    }

    // --- Layer 3: Shockwave rings ---
    this._updateAndDrawRings(cx, cy, canvas.width, canvas.height);
  }

  /**
   * Draw a regular hexagon centered at (cx, cy).
   */
  _drawHexagon(cx, cy, radius) {
    const { ctx } = this;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6;
      const x = cx + Math.cos(angle) * radius;
      const y = cy + Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  }

  /**
   * Spawn a new shockwave ring at the center.
   */
  _spawnRing(minDim) {
    if (this.rings.length >= MAX_RINGS) return;

    this.rings.push({
      radius: minDim * this.config.innerRadius,
      hueBase: (this.hueOffset + Math.random() * 60) % 360,
    });
  }

  /**
   * Update ring positions and draw them. Remove rings that exit the canvas.
   */
  _updateAndDrawRings(cx, cy, w, h) {
    const { ctx, config } = this;
    const segCount = config.ringSegments;
    const thickness = config.ringThickness;
    const speed = config.ringSpeed + this.bassEnergy * 3;

    // Corner distance — rings beyond this are fully off-screen
    const maxDist = Math.sqrt(cx * cx + cy * cy) + thickness;

    const angleStep = (Math.PI * 2) / segCount;
    // Gap between segments (10% of angular step)
    const gapFraction = 0.10;
    const segHalfWidth = (angleStep * (1 - gapFraction)) / 2;

    for (let r = this.rings.length - 1; r >= 0; r--) {
      const ring = this.rings[r];
      ring.radius += speed;

      // Remove rings that have fully exited the canvas
      if (ring.radius - thickness > maxDist) {
        this.rings.splice(r, 1);
        continue;
      }

      const innerR = ring.radius;
      const outerR = ring.radius + thickness;

      for (let s = 0; s < segCount; s++) {
        const angle = angleStep * s;
        const a1 = angle - segHalfWidth;
        const a2 = angle + segHalfWidth;

        const x1 = cx + Math.cos(a1) * innerR;
        const y1 = cy + Math.sin(a1) * innerR;
        const x2 = cx + Math.cos(a2) * innerR;
        const y2 = cy + Math.sin(a2) * innerR;
        const x3 = cx + Math.cos(a2) * outerR;
        const y3 = cy + Math.sin(a2) * outerR;
        const x4 = cx + Math.cos(a1) * outerR;
        const y4 = cy + Math.sin(a1) * outerR;

        // Each segment gets a slightly different hue for color variety
        const hue = (ring.hueBase + (s / segCount) * 60) % 360;
        ctx.fillStyle = `hsl(${hue | 0}, 90%, 55%)`;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.lineTo(x3, y3);
        ctx.lineTo(x4, y4);
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  /**
   * Clear canvas and reset all runtime state.
   */
  clear() {
    super.clear();
    this.rings.length = 0;
    this.hueOffset = 0;
  }
}

// --- Utility: hex color to HSL ---

function hexToHSL(hex) {
  if (!hex || hex.length < 7) return { h: 0, s: 0, l: 50 };

  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  if (isNaN(r) || isNaN(g) || isNaN(b)) return { h: 0, s: 0, l: 50 };

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2 * 100;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 50
    ? (d / (2 - max - min)) * 100
    : (d / (max + min)) * 100;

  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;

  return { h, s, l };
}

// Register with the visualizer registry
VisualizerRegistry.register(ColorExplosion);

export { ColorExplosion };
export default ColorExplosion;
